import React, { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabaseClient'
import type { CompanyRow, HotelRow, RoomTypeRow } from '@/lib/types'
import { Button, Card, Checkbox, PageSpinner, useToast } from '@/components/ui'
import { useEvent } from '@/hooks/useEvent'
import { buildCompanyTemplate } from '@/lib/excel/companyTemplate'
import { buildCompanyExport } from '@/lib/excel/exportBuilder'
import { parseWorkbookFile } from '@/lib/excel/shared'
import { buildImportPreview, ImportPreview } from '@/lib/excel/importPreview'
import { applyImport, attachKnownIds } from '@/lib/excel/applyImport'
import ImportPreviewPanel, { importHasBlockingIssues } from '@/components/import/ImportPreviewPanel'

interface Props {
  eventId: string
  companyId: string
  companyName: string
  companyCode: string
}

/**
 * Per-company Import / Export tab (sections 34, 43-44, 68-70). Two Excel
 * workflows meet here: downloading a pre-filled template / final rooming
 * list, and re-importing a single company's file with a preview the admin
 * must resolve before anything is written -- never a silent overwrite.
 */
export default function ImportExportTab({ eventId, companyId, companyName, companyCode }: Props) {
  const { event } = useEvent()
  const { push } = useToast()
  const [company, setCompany] = useState<CompanyRow | null>(null)
  const [hotels, setHotels] = useState<HotelRow[]>([])
  const [roomTypes, setRoomTypes] = useState<RoomTypeRow[]>([])
  const [busy, setBusy] = useState<string | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [fileName, setFileName] = useState('')
  const [applying, setApplying] = useState(false)
  const [bypassDuplicates, setBypassDuplicates] = useState(false)

  useEffect(() => {
    (async () => {
      const [{ data: c }, { data: h }, { data: rt }] = await Promise.all([
        supabase.from('companies').select('*').eq('id', companyId).maybeSingle(),
        supabase.from('hotels').select('*').eq('event_id', eventId).eq('is_active', true).order('name'),
        supabase.from('room_types').select('*').eq('event_id', eventId).eq('is_active', true).order('name'),
      ])
      setCompany((c as CompanyRow) || null)
      setHotels((h as HotelRow[]) || [])
      setRoomTypes((rt as RoomTypeRow[]) || [])
    })()
  }, [companyId, eventId])

  async function downloadTemplate() {
    if (!company) return
    setBusy('template')
    try {
      await buildCompanyTemplate({
        companyId,
        eventName: event.name,
        companyCode: company.code,
        companyName: company.name,
        contactPerson: company.contact_person,
        contactMobile: company.contact_mobile,
        contactEmail: company.contact_email,
      })
    } catch (e: any) {
      push(e.message || 'Could not build template', 'error')
    } finally {
      setBusy(null)
    }
  }

  async function downloadExport() {
    setBusy('export')
    try {
      await buildCompanyExport({ eventId, companyId }, companyName, event.name)
    } catch (e: any) {
      push(e.message || 'Could not build export', 'error')
    } finally {
      setBusy(null)
    }
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setFileName(file.name)
    setBusy('parsing')
    try {
      const sheets = await parseWorkbookFile(file)
      const existingCodes = new Set(company ? [company.code] : [])
      let p = buildImportPreview(sheets, { hotels, roomTypes, companyCodes: existingCodes })

      // A per-company import stays scoped to this company -- any row naming
      // a different company code is rejected rather than silently applied
      // to (or creating) some other company's data.
      const foreignRows =
        p.companies.some((c) => c.code !== companyCode) ||
        p.allocations.some((a) => a.company_code !== companyCode) ||
        p.guests.some((g) => g.company_code !== companyCode)
      if (foreignRows) {
        p = {
          ...p,
          issues: [
            { sheet: 'workbook', row: 0, severity: 'error', message: `This file references a company other than ${companyCode}. Use Master Bulk Import on the event's Import / Export page for multi-company files.` },
            ...p.issues,
          ],
        }
      }

      p = attachKnownIds(p, hotels, roomTypes)
      setPreview(p)
    } catch (err: any) {
      push(err.message || 'Could not read that file', 'error')
    } finally {
      setBusy(null)
    }
  }

  async function apply() {
    if (!preview) return
    setApplying(true)
    const result = await applyImport({ eventId, companyId, fileName, workflow: 'company', preview, bypassDuplicates })
    setApplying(false)
    if (!result.success) {
      push(result.error_detail || result.error_code || 'Import failed', 'error')
      return
    }
    push(`Import applied — ${result.allocations_applied ?? 0} allocation(s), ${result.guests_written ?? 0} guest(s) written`)
    setPreview(null)
    setFileName('')
  }

  if (!company) return <PageSpinner label="Loading…" />

  return (
    <div className="max-w-3xl space-y-6">
      <Card className="p-5">
        <h3 className="mb-1 text-sm font-semibold text-slate-900">Export</h3>
        <p className="mb-3 text-sm text-slate-500">Download this company's current allocation for editing offline, or a finished rooming list export.</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" loading={busy === 'template'} onClick={downloadTemplate}>
            Download Editable Template
          </Button>
          <Button variant="secondary" loading={busy === 'export'} onClick={downloadExport}>
            Export Rooming List (.xlsx)
          </Button>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="mb-1 text-sm font-semibold text-slate-900">Import</h3>
        <p className="mb-3 text-sm text-slate-500">
          Upload the template above after filling in guest names, or any company-scoped Excel file. You'll see a full preview before anything is saved.
        </p>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50">
          <input type="file" accept=".xlsx" className="hidden" onChange={handleFile} />
          {busy === 'parsing' ? 'Reading file…' : fileName || 'Choose an .xlsx file…'}
        </label>
      </Card>

      {preview && (
        <Card className="p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Preview: {fileName}</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPreview(null)
                setFileName('')
              }}
            >
              Cancel
            </Button>
          </div>
          <ImportPreviewPanel preview={preview} hotels={hotels} roomTypes={roomTypes} onChangePreview={setPreview} />

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
            <Checkbox label="Allow duplicate mobile numbers (admin override)" checked={bypassDuplicates} onChange={(e) => setBypassDuplicates(e.target.checked)} />
            <Button variant="primary" loading={applying} disabled={importHasBlockingIssues(preview)} onClick={apply}>
              Apply Import
            </Button>
          </div>
          {importHasBlockingIssues(preview) && <p className="mt-2 text-xs text-red-600">Fix all errors and resolve all unrecognized names before applying.</p>}
        </Card>
      )}
    </div>
  )
}
